import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { useSlidingWindow } from '../../hooks/useSlidingWindow';
import { Project } from '../../types';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Shield, Check } from 'lucide-react';
import { navigateToProject } from '../../utils/navigationUtils';
import { isDemoMode } from '../../services/dataScope';
import SlidingWindowNav from './SlidingWindowNav';
import {
  DASHBOARD_WINDOW_LIST,
  DASHBOARD_WINDOW_ROW,
  DASHBOARD_WINDOW_SIZE,
} from './dashboardWindowLayout';

interface RightsWatchCardProps {
  projects: Project[];
  onMarkChecked: (projectId: string) => void;
  checkingId?: string | null;
}

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const RightsWatchCard: React.FC<RightsWatchCardProps> = ({
  projects,
  onMarkChecked,
  checkingId = null,
}) => {
  const { t } = useI18n();
  const { formatDateShort } = useTranslations();
  const navigate = useNavigate();
  const interactive = !isDemoMode();
  const today = startOfToday();
  const { visible, canPrev, canNext, prev, next, showNav } = useSlidingWindow(
    projects,
    DASHBOARD_WINDOW_SIZE
  );
  const hasItems = projects.length > 0;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 min-h-7">
          <CardTitle className="flex items-center text-base min-w-0">
            <Shield className="w-5 h-5 text-purple-500 mr-2 shrink-0" />
            <span className="truncate">{t('dashboard.rightsWatchTitle')}</span>
          </CardTitle>
          {showNav && (
            <SlidingWindowNav
              canPrev={canPrev}
              canNext={canNext}
              onPrev={prev}
              onNext={next}
              tone="purple"
            />
          )}
        </div>
      </CardHeader>
      <CardContent className={hasItems ? 'flex-1' : 'flex-1 flex items-center justify-center'}>
        {hasItems ? (
          <div className={DASHBOARD_WINDOW_LIST}>
            {visible.map((project) => {
              const endDate = project.rights?.endDate
                ? new Date(project.rights.endDate)
                : null;
              const endDay = endDate
                ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
                : null;
              const isExpired = !!endDay && endDay.getTime() < today.getTime();
              const isChecking = checkingId === project.id;
              const projectLabel = (
                <>
                  <p className="font-semibold text-gray-900 truncate">{project.name}</p>
                  <p className="text-sm text-gray-600 mt-0.5 truncate">
                    {endDate
                      ? isExpired
                        ? t('dashboard.rightsExpiredOn', { date: formatDateShort(endDate) })
                        : t('dashboard.rightsUntil', { date: formatDateShort(endDate) })
                      : t('dashboard.rightsWatchSubtitle')}
                  </p>
                </>
              );

              return (
                <div
                  key={project.id}
                  className={`flex items-center justify-between gap-2 px-3 ${DASHBOARD_WINDOW_ROW} overflow-hidden rounded-xl border transition-colors duration-150 ${
                    isExpired
                      ? 'bg-red-50/80 border-red-200/60 hover:bg-red-50'
                      : 'bg-purple-50/80 border-purple-200/60 hover:bg-purple-50'
                  }`}
                >
                  {interactive ? (
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => navigateToProject(navigate, project.id)}
                    >
                      {projectLabel}
                    </button>
                  ) : (
                    <div className="min-w-0 flex-1 text-left">{projectLabel}</div>
                  )}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span
                      className={`hidden sm:inline-flex items-center font-medium rounded-full px-2 py-0.5 text-xs ${
                        isExpired
                          ? 'bg-red-100 text-red-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {isExpired ? t('dashboard.rightsExpired') : t('dashboard.rightsInProgress')}
                    </span>
                    <button
                      type="button"
                      disabled={isChecking}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkChecked(project.id);
                      }}
                      className={`inline-flex items-center gap-1 h-8 px-2 rounded-lg text-sm font-medium
                        transition-colors duration-150
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0
                        disabled:opacity-50 disabled:pointer-events-none
                        ${
                          isExpired
                            ? 'text-red-700 hover:text-red-800 hover:bg-red-100/80 active:bg-red-100 focus-visible:ring-red-500/40'
                            : 'text-purple-700 hover:text-purple-800 hover:bg-purple-100/80 active:bg-purple-100 focus-visible:ring-purple-500/40'
                        }`}
                    >
                      <Check className="w-4 h-4" strokeWidth={2.25} />
                      <span>{t('dashboard.rightsMarkChecked')}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-center">{t('dashboard.noRightsToWatch')}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default RightsWatchCard;
